import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext, STREAM_URL } from '../App';
import { motion, AnimatePresence } from 'framer-motion';

type Mode = 'interview' | 'meeting' | 'general';
type Provider = 'bedrock' | 'anthropic';
type Language = 'tr' | 'en';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface Settings {
  aiProvider: Provider;
  preferredModel: string;
  language: string;
}

interface Document {
  docId: string;
  title: string;
  docType: 'cv' | 'interview' | 'meeting' | 'general';
  company?: string;
  position?: string;
  notes?: string;
  uploadedAt: string;
}

const Dashboard: React.FC = () => {
  const auth = useContext(AuthContext);
  const [mode, setMode] = useState<Mode>('interview');
  const [language, setLanguage] = useState<Language>('tr');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<Settings>({ aiProvider: 'bedrock', preferredModel: 'opus', language: 'auto' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Döküman yönetimi state'leri
  const [showDocuments, setShowDocuments] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newDoc, setNewDoc] = useState({
    title: '',
    docType: 'cv' as 'cv' | 'interview' | 'meeting' | 'general',
    company: '',
    position: '',
    notes: '',
    content: ''
  });
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);
  const isProcessingRef = useRef(false);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const accumulatedTextRef = useRef('');
  const questionQueueRef = useRef<string[]>([]); // Soru kuyruğu

  // Soru kalıpları
  const questionPatterns = [
    // Türkçe
    'ne ', 'nasıl ', 'neden ', 'niye ', 'hangi ', 'kim ', 'nerede ', 'ne zaman ', 'kaç ',
    ' mi', ' mı', ' mu', ' mü', 'mısın', 'misin', 'musun', 'müsün',
    'anlat', 'açıkla', 'söyle', 'tarif et', 'bahset', 'nedir', 'edir',
    // İngilizce
    'what ', 'how ', 'why ', 'which ', 'who ', 'where ', 'when ',
    'can you', 'could you', 'would you', 'tell me', 'explain', 'describe'
  ];

  useEffect(() => {
    loadSettings();
    initSpeechRecognition(language);
    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  // Dil değiştiğinde recognition'ı yeniden başlat
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    initSpeechRecognition(language);
  }, [language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Sync refs with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  const loadSettings = async () => {
    try {
      const data = await auth?.apiCall('/chat', {
        method: 'POST',
        body: JSON.stringify({ action: 'get_settings' })
      });
      if (data?.settings) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Settings load error:', error);
    }
  };

  const isQuestion = (text: string): boolean => {
    const lowerText = text.toLowerCase().trim();
    if (lowerText.includes('?')) return true;
    for (const pattern of questionPatterns) {
      if (lowerText.includes(pattern)) return true;
    }
    return false;
  };

  // Kuyruktan sonraki soruyu işle
  const processNextInQueue = () => {
    if (questionQueueRef.current.length > 0 && !isProcessingRef.current) {
      const nextQuestion = questionQueueRef.current.shift()!;
      processQuestionInternal(nextQuestion);
    }
  };

  // Ana soru işleme fonksiyonu (dışarıdan çağrılır)
  const processQuestion = async (question: string) => {
    if (!question.trim()) return;

    // İşlem devam ediyorsa kuyruğa ekle
    if (isProcessingRef.current) {
      console.log('İşlem devam ediyor, kuyruğa eklendi:', question);
      questionQueueRef.current.push(question);
      return;
    }

    // Hemen işle
    processQuestionInternal(question);
  };

  // İç işleme fonksiyonu
  const processQuestionInternal = async (question: string) => {
    // Dinlemeyi DURDURMUYORUZ - sadece işleme başlıyoruz
    setIsProcessing(true);
    isProcessingRef.current = true;

    // Transkripti temizle (yeni konuşma için)
    setTranscript('');
    accumulatedTextRef.current = '';

    // Unique ID ile mesajları takip et
    const uniqueId = Date.now().toString();

    // Kullanıcı mesajı ve boş assistant mesajı aynı anda ekle
    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: question,
        timestamp: new Date()
      },
      {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        _id: uniqueId // Geçici ID
      } as Message
    ]);

    try {
      const response = await fetch(STREAM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({
          message: question,
          mode,
          language,
          history: messages.slice(-10).map(m => ({ role: m.role, content: m.content }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sunucu hatası');
      }

      const text = await response.text();
      const lines = text.split('\n');
      let fullContent = '';

      for (const line of lines) {
        if (line.startsWith('data: ') && !line.includes('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              fullContent += data.text;
              // En son boş assistant mesajını güncelle (ID ile eşleştir)
              setMessages(prev => {
                const newMessages = [...prev];
                // Son eklenen ve bu unique ID'ye sahip assistant mesajını bul
                for (let i = newMessages.length - 1; i >= 0; i--) {
                  if ((newMessages[i] as any)._id === uniqueId) {
                    newMessages[i] = {
                      ...newMessages[i],
                      content: fullContent
                    };
                    break;
                  }
                }
                return newMessages;
              });
            }
          } catch (e) {
            // JSON parse hatası, devam et
          }
        }
      }
    } catch (error: any) {
      console.error('Quick answer error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        for (let i = newMessages.length - 1; i >= 0; i--) {
          if ((newMessages[i] as any)._id === uniqueId) {
            newMessages[i] = {
              ...newMessages[i],
              content: `Hata: ${error.message || 'Bağlantı hatası'}`
            };
            break;
          }
        }
        return newMessages;
      });
    }

    setIsProcessing(false);
    isProcessingRef.current = false;

    // Kuyrukta bekleyen soru varsa işle
    setTimeout(() => processNextInQueue(), 100);
  };

  const initSpeechRecognition = (lang: Language) => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('Speech recognition not supported');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    // Türkçe modunda hem Türkçe hem İngilizce kelimeleri anlasın diye tr-TR kullan
    // İngilizce modunda en-US kullan
    recognition.lang = lang === 'en' ? 'en-US' : 'tr-TR';

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += result + ' ';
        } else {
          interimTranscript += result;
        }
      }

      // Birikmiş metne ekle
      if (finalTranscript) {
        accumulatedTextRef.current += finalTranscript;
      }

      // Ekranda göster
      const displayText = accumulatedTextRef.current + interimTranscript;
      setTranscript(displayText);

      // Sessizlik zamanlayıcısını sıfırla
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      // Final transcript geldiğinde zamanlayıcı başlat
      if (finalTranscript) {
        silenceTimerRef.current = setTimeout(() => {
          const accumulated = accumulatedTextRef.current.trim();
          // Soru olsun olmasın, sessizlik sonrası cevap ver
          if (accumulated && !isProcessingRef.current) {
            processQuestion(accumulated);
          }
        }, 1500); // 1.5 saniye sessizlik
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech error:', event.error);
      if (event.error === 'no-speech' || event.error === 'audio-capture') {
        // Sessizlik hatası - yeniden başlat
        if (isListeningRef.current && !isProcessingRef.current) {
          try {
            recognition.start();
          } catch (e) {}
        }
      }
    };

    recognition.onend = () => {
      // Sadece dinleme modundaysa ve işlenmiyorsa yeniden başlat
      if (isListeningRef.current && !isProcessingRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.log('Restart error:', e);
        }
      }
    };

    recognitionRef.current = recognition;
  };

  const toggleListening = () => {
    if (isListening) {
      // Durdur
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      setIsListening(false);
      isListeningRef.current = false;
      setTranscript('');
      accumulatedTextRef.current = '';
    } else {
      // Başlat
      setTranscript('');
      accumulatedTextRef.current = '';

      try {
        recognitionRef.current?.start();
        setIsListening(true);
        isListeningRef.current = true;
      } catch (e) {
        console.error('Start error:', e);
      }
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    const currentInput = input;
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Streaming için boş assistant mesajı ekle
    const assistantMsgIndex = messages.length + 1;
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      timestamp: new Date()
    }]);

    try {
      const response = await fetch(STREAM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({
          message: currentInput,
          mode,
          language,
          history: messages.map(m => ({ role: m.role, content: m.content }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sunucu hatası');
      }

      const text = await response.text();
      const lines = text.split('\n');
      let fullContent = '';

      for (const line of lines) {
        if (line.startsWith('data: ') && !line.includes('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              fullContent += data.text;
              // Her chunk'ta mesajı güncelle
              setMessages(prev => {
                const newMessages = [...prev];
                if (newMessages[assistantMsgIndex]) {
                  newMessages[assistantMsgIndex] = {
                    ...newMessages[assistantMsgIndex],
                    content: fullContent
                  };
                }
                return newMessages;
              });
            }
          } catch (e) {
            // JSON parse hatası, devam et
          }
        }
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[assistantMsgIndex]) {
          newMessages[assistantMsgIndex] = {
            ...newMessages[assistantMsgIndex],
            content: `Hata: ${error.message || 'Bağlantı hatası'}`
          };
        }
        return newMessages;
      });
    }

    setLoading(false);
  };

  const updateSettings = async (newSettings: Partial<Settings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);

    try {
      await auth?.apiCall('/chat', {
        method: 'POST',
        body: JSON.stringify({ action: 'update_settings', ...newSettings })
      });
    } catch (error) {
      console.error('Settings update error:', error);
    }
  };

  // Döküman yönetimi fonksiyonları
  const loadDocuments = async () => {
    setDocsLoading(true);
    try {
      const data = await auth?.apiCall('/docs', {
        method: 'POST',
        body: JSON.stringify({ action: 'list' })
      });
      if (data?.documents) {
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error('Docs load error:', error);
    }
    setDocsLoading(false);
  };

  const uploadDocument = async () => {
    if (!newDoc.title.trim() || !newDoc.content.trim()) {
      alert('Başlık ve içerik gerekli!');
      return;
    }

    setUploadingDoc(true);
    try {
      const data = await auth?.apiCall('/docs', {
        method: 'POST',
        body: JSON.stringify({
          action: 'upload',
          title: newDoc.title,
          docType: newDoc.docType,
          company: newDoc.company || undefined,
          position: newDoc.position || undefined,
          notes: newDoc.notes || undefined,
          content: newDoc.content
        })
      });

      if (data?.success) {
        setNewDoc({ title: '', docType: 'cv', company: '', position: '', notes: '', content: '' });
        loadDocuments();
      } else {
        alert(data?.error || 'Yükleme hatası!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Yükleme başarısız!');
    }
    setUploadingDoc(false);
  };

  const deleteDocument = async (docId: string) => {
    if (!window.confirm('Bu dökümanı silmek istediğinize emin misiniz?')) return;

    try {
      const data = await auth?.apiCall('/docs', {
        method: 'POST',
        body: JSON.stringify({ action: 'delete', docId })
      });

      if (data?.success) {
        loadDocuments();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setNewDoc(prev => ({
        ...prev,
        title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
        content: content
      }));
    };
    reader.readAsText(file);
  };

  const docTypeConfig = {
    cv: { label: 'CV / Özgeçmiş', icon: '📄', color: 'bg-blue-500' },
    interview: { label: 'Mülakat Notu', icon: '💼', color: 'bg-orange-500' },
    meeting: { label: 'Toplantı Notu', icon: '📅', color: 'bg-green-500' },
    general: { label: 'Genel Döküman', icon: '📋', color: 'bg-purple-500' }
  };

  const modeConfig = {
    interview: { label: 'Mülakat', icon: '💼', color: 'from-orange-500 to-amber-500', border: 'border-orange-500' },
    meeting: { label: 'Toplantı', icon: '📅', color: 'from-blue-500 to-cyan-500', border: 'border-blue-500' },
    general: { label: 'Genel', icon: '💡', color: 'from-green-500 to-emerald-500', border: 'border-green-500' }
  };

  // Geçmiş konuşmaları yükle
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await auth?.apiCall('/chat', {
        method: 'POST',
        body: JSON.stringify({ action: 'get_history' })
      });
      if (data?.sessions) {
        setConversationHistory(data.sessions);
      }
    } catch (error) {
      console.error('History load error:', error);
    }
    setHistoryLoading(false);
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Sidebar - Fixed */}
      <aside className="w-72 glass-dark border-r border-dark-700 flex flex-col flex-shrink-0 overflow-y-auto">
        <div className="p-6 border-b border-dark-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-lg">
              {auth?.user?.displayName?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="font-semibold text-white">{auth?.user?.displayName}</h3>
              <p className="text-sm text-dark-400">Aktif</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <h4 className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Mod Seçimi</h4>
          <div className="space-y-2">
            {(Object.keys(modeConfig) as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  mode === m
                    ? `bg-gradient-to-r ${modeConfig[m].color} text-white shadow-lg`
                    : 'hover:bg-dark-700 text-dark-300'
                }`}
              >
                <span className="text-xl">{modeConfig[m].icon}</span>
                <span className="font-medium">{modeConfig[m].label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-dark-700">
          <h4 className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Dil / Language</h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setLanguage('tr')}
              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl transition-all ${
                language === 'tr'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                  : 'bg-dark-800/50 text-dark-400 hover:bg-dark-700'
              }`}
            >
              <span>🇹🇷</span>
              <span className="text-sm font-medium">Türkçe</span>
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl transition-all ${
                language === 'en'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'bg-dark-800/50 text-dark-400 hover:bg-dark-700'
              }`}
            >
              <span>🇬🇧</span>
              <span className="text-sm font-medium">English</span>
            </button>
          </div>
          <p className="text-xs text-dark-500 mt-2">
            {language === 'tr' ? 'Plaza dili desteklenir (teknik terimler)' : 'Answers will be in English'}
          </p>
        </div>

        <div className="p-4 border-t border-dark-700">
          <div className="p-3 rounded-xl bg-dark-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-dark-400">AI Provider</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                settings.aiProvider === 'bedrock' ? 'bg-orange-500/20 text-orange-400' : 'bg-purple-500/20 text-purple-400'
              }`}>
                {settings.aiProvider === 'bedrock' ? 'AWS Bedrock' : 'Anthropic API'}
              </span>
            </div>
            <p className="text-xs text-dark-500">{settings.aiProvider === 'bedrock' ? 'Claude 3.5 Sonnet' : 'Claude Sonnet 4'}</p>
          </div>
        </div>

        <div className="mt-auto p-4 border-t border-dark-700 space-y-2">
          <button
            onClick={() => { setShowHistory(true); loadHistory(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-700 text-dark-300 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Geçmiş</span>
          </button>
          <button
            onClick={() => { setShowDocuments(true); loadDocuments(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-700 text-dark-300 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Dökümanlarım</span>
            {documents.length > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded-full">
                {documents.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-700 text-dark-300 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Ayarlar</span>
          </button>
          <button
            onClick={auth?.logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 text-red-400 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Çıkış</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header - Fixed at top */}
        <header className="h-16 glass-dark border-b border-dark-700 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className={`h-8 w-1 rounded-full bg-gradient-to-b ${modeConfig[mode].color}`}></div>
            <div>
              <h1 className="font-semibold text-white">{modeConfig[mode].icon} {modeConfig[mode].label} Modu</h1>
              <p className="text-xs text-dark-400">
                {isListening ? (isProcessing ? `⏳ Cevap alınıyor...${questionQueueRef.current.length > 0 ? ` (${questionQueueRef.current.length} soru bekliyor)` : ''}` : '🎙️ Dinliyor - 1.5 sn sessizlikte cevap') : 'Başlat\'a tıkla'}
              </p>
            </div>
          </div>

          <button
            onClick={toggleListening}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              isListening
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            {isListening && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
            )}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            {isProcessing ? 'Cevap alınıyor...' : isListening ? 'Dinleniyor' : 'Başlat'}
          </button>
        </header>

        {/* Transcript Display - Cevap alınırken de göster */}
        <AnimatePresence>
          {isListening && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-6 py-4 bg-dark-800/50 border-b border-dark-700 flex-shrink-0"
            >
              <div className="flex items-start gap-3">
                <div className="flex gap-1 mt-1">
                  {[0, 1, 2, 3, 4].map(i => (
                    <motion.div
                      key={i}
                      animate={{ scaleY: [1, 1.8, 1] }}
                      transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.1 }}
                      className="w-1 h-4 bg-red-500 rounded-full origin-center"
                    />
                  ))}
                </div>
                <div className="flex-1 min-h-[24px]">
                  {transcript ? (
                    <p className="text-white">{transcript}</p>
                  ) : (
                    <p className="text-dark-400 italic">Konuşmaya başlayın...</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {messages.length === 0 && !isListening && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 rounded-2xl bg-dark-800 flex items-center justify-center mb-4">
                <span className="text-4xl">{modeConfig[mode].icon}</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {modeConfig[mode].label} Moduna Hoş Geldiniz
              </h3>
              <p className="text-dark-400 max-w-md mb-4">
                "Başlat" butonuna tıklayın. Konuşun, 1.5 sn susunca cevap gelecek.
                Cevaplar ekranda kalır, dinleme devam eder.
              </p>
              <div className="text-sm text-dark-500 bg-dark-800/50 rounded-lg p-4 max-w-md">
                <p className="font-medium text-dark-300 mb-2">Nasıl çalışır:</p>
                <ul className="text-left space-y-1">
                  <li>• Başlat'a bas, konuş</li>
                  <li>• 1.5 sn sus → Cevap gelir</li>
                  <li>• Cevaplar yukarıda birikir</li>
                  <li>• Dinleme devam eder</li>
                </ul>
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-2xl px-5 py-4 rounded-2xl ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white rounded-br-md'
                  : 'glass text-dark-100 rounded-bl-md'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-primary-200' : 'text-dark-400'}`}>
                  {msg.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </motion.div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="glass px-5 py-4 rounded-2xl rounded-bl-md">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Fixed at bottom */}
        <div className="p-6 border-t border-dark-700 glass-dark flex-shrink-0">
          <div className="flex gap-4">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Mesajınızı yazın..."
              className="flex-1 px-5 py-4 rounded-xl bg-dark-800/50 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="px-6 py-4 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-medium shadow-lg shadow-primary-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </main>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-3xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Ayarlar</h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-3">AI Provider</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => updateSettings({ aiProvider: 'bedrock' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        settings.aiProvider === 'bedrock'
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-dark-600 hover:border-dark-500'
                      }`}
                    >
                      <div className="text-2xl mb-2">☁️</div>
                      <div className="font-medium text-white">AWS Bedrock</div>
                      <div className="text-xs text-dark-400">IAM Auth</div>
                    </button>
                    <button
                      onClick={() => updateSettings({ aiProvider: 'anthropic' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        settings.aiProvider === 'anthropic'
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-dark-600 hover:border-dark-500'
                      }`}
                    >
                      <div className="text-2xl mb-2">🔑</div>
                      <div className="font-medium text-white">Anthropic</div>
                      <div className="text-xs text-dark-400">API Key</div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-full py-3 px-4 rounded-xl bg-dark-700 hover:bg-dark-600 text-white font-medium transition-all"
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Documents Modal */}
      <AnimatePresence>
        {showDocuments && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDocuments(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Dökümanlarım</h2>
                <button
                  onClick={() => setShowDocuments(false)}
                  className="text-dark-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-dark-400 mb-6">
                CV, mülakat notları ve toplantı dökümanlarınızı yükleyin. AI, sorulara cevap verirken bu dökümanlardan faydalanacak.
              </p>

              {/* Yeni Döküman Ekleme Formu */}
              <div className="bg-dark-800/50 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Yeni Döküman Ekle</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-dark-300 mb-2">Başlık *</label>
                    <input
                      type="text"
                      value={newDoc.title}
                      onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="Örn: Software Engineer CV"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-dark-300 mb-2">Döküman Tipi</label>
                    <select
                      value={newDoc.docType}
                      onChange={(e) => setNewDoc({ ...newDoc, docType: e.target.value as any })}
                      className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="cv">📄 CV / Özgeçmiş</option>
                      <option value="interview">💼 Mülakat Notu</option>
                      <option value="meeting">📅 Toplantı Notu</option>
                      <option value="general">📋 Genel Döküman</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-dark-300 mb-2">Şirket (opsiyonel)</label>
                    <input
                      type="text"
                      value={newDoc.company}
                      onChange={(e) => setNewDoc({ ...newDoc, company: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="Örn: Amazon, Google"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-dark-300 mb-2">Pozisyon (opsiyonel)</label>
                    <input
                      type="text"
                      value={newDoc.position}
                      onChange={(e) => setNewDoc({ ...newDoc, position: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="Örn: Senior Software Engineer"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-dark-300 mb-2">Notlar (opsiyonel)</label>
                  <input
                    type="text"
                    value={newDoc.notes}
                    onChange={(e) => setNewDoc({ ...newDoc, notes: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Örn: 2024 güncel versiyon"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-dark-300 mb-2">İçerik *</label>
                  <div className="p-3 mb-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                    ⚠️ <strong>Önemli:</strong> PDF dosyaları desteklenmiyor! CV'nizi kopyala-yapıştır ile metin olarak ekleyin.
                  </div>
                  <textarea
                    value={newDoc.content}
                    onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                    rows={8}
                    className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-600 text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                    placeholder="CV içeriğinizi buraya YAPIŞTIRIN. Örnek:

İsim: Oğuzhan Hızıroğlu
Deneyim: 5 yıl yazılım geliştirme
Beceriler: React, Node.js, AWS, Python
Projeler:
- Yüz tanıma sistemi geliştirdim
- E-ticaret platformu kurdum
..."
                  />
                </div>

                <button
                  onClick={uploadDocument}
                  disabled={uploadingDoc || !newDoc.title.trim() || !newDoc.content.trim()}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-medium shadow-lg shadow-primary-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadingDoc ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Döküman Ekle
                    </>
                  )}
                </button>
              </div>

              {/* Mevcut Dökümanlar */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Yüklü Dökümanlar</h3>

                {docsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-dark-400">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>Henüz döküman yüklenmemiş</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.docId} className="bg-dark-800/50 rounded-xl p-4 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${docTypeConfig[doc.docType]?.color || 'bg-gray-500'} flex items-center justify-center text-lg`}>
                          {docTypeConfig[doc.docType]?.icon || '📄'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{doc.title}</h4>
                          <div className="flex items-center gap-2 text-sm text-dark-400">
                            <span>{docTypeConfig[doc.docType]?.label || 'Döküman'}</span>
                            {doc.company && <span>• {doc.company}</span>}
                            {doc.position && <span>• {doc.position}</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteDocument(doc.docId)}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-dark-400 hover:text-red-400 transition-all"
                          title="Sil"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Konuşma Geçmişi</h2>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-dark-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : conversationHistory.length === 0 ? (
                <div className="text-center py-8 text-dark-400">
                  <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>Henüz konuşma geçmişi yok</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {conversationHistory.map((session, idx) => (
                    <div key={idx} className="bg-dark-800/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          session.mode === 'interview' ? 'bg-orange-500/20 text-orange-400' :
                          session.mode === 'meeting' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {session.mode === 'interview' ? '💼 Mülakat' :
                           session.mode === 'meeting' ? '📅 Toplantı' : '💡 Genel'}
                        </span>
                        <span className="text-xs text-dark-500">
                          {new Date(session.createdAt).toLocaleString('tr-TR')}
                        </span>
                        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${
                          session.provider === 'anthropic' ? 'bg-purple-500/20 text-purple-400' : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {session.provider === 'anthropic' ? 'Anthropic' : 'Bedrock'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <span className="text-primary-400 font-medium text-sm">Sen:</span>
                          <p className="text-dark-200 text-sm">{session.userMessage}</p>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-secondary-400 font-medium text-sm">AI:</span>
                          <p className="text-dark-300 text-sm">{session.assistantMessage}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
